import { useState, useEffect } from 'react';

import { getClientCookie, setClientCookie } from '~/app/lib/utils/cookie';
import { MUST_ACCEPT_PRIVACY_POLICY_COUNTRIES } from '~/app/lib/privacy';
import { AddonsConfig, AddonTypes } from '~/app/lib/songwhipApi/types';
import { selectUserCountry } from '~/app/lib/store/session/selectors';
import { resolveAgeFromTimestamp } from '~/app/lib/utils/date';
import { useSelector } from '~/app/lib/store/redux';

import {
  DOB_COOKIE_NAME,
  AUTH_GATE_COOKIE_NAME,
  PRIVACY_POLICY_COOKIE_NAME,
} from '~/config';

import { DEFAULT_TERRITORY_CODE } from '../../ItemPageEdit/components/AgeGateSettingsDialog/constants';
import { useCountryBlocked } from '../../hooks/useCountryBlocked';
import { ItemContext } from '../../types';
import { GateTypes } from './types';

type AcceptedCookieNames =
  | typeof PRIVACY_POLICY_COOKIE_NAME
  | typeof DOB_COOKIE_NAME
  | typeof AUTH_GATE_COOKIE_NAME;

export type UsePageGateResult = {
  type?: GateTypes;
  unlock: typeof setClientCookie;
};

export const usePageGate = ({
  data: { item },
  addons: { AGE_GATE, AUTH_GATE },
}: ItemContext): UsePageGateResult => {
  const [cookieConfig, setCookieConfig] = useState<Partial<
    Record<AcceptedCookieNames, string>
  > | null>(null);

  const country = useSelector(selectUserCountry);
  const isCountryBlocked = useCountryBlocked();

  const isPrivacyPolicyGated = getIsPrivacyPolicyGated({
    cookie: cookieConfig?.[PRIVACY_POLICY_COOKIE_NAME],
    country,
  });

  const { isAgeBlocked, isAgeGated } = getIsAgeGated({
    cookie: cookieConfig?.[DOB_COOKIE_NAME],
    country,
    addon: AGE_GATE,
  });

  const isAuthGated = getIsAuthGated({
    cookie: cookieConfig?.[AUTH_GATE_COOKIE_NAME],
    pagePath: item.pagePath,
    addon: AUTH_GATE,
  });

  // Order means priority
  const gateType = (() => {
    if (isCountryBlocked) {
      return GateTypes.COUNTRY_BLOCKED;
    }

    if (isPrivacyPolicyGated) {
      return GateTypes.PRIVACY_POLICY_GATE;
    }

    if (isAgeBlocked) {
      return GateTypes.AGE_BLOCKED;
    }

    if (isAgeGated) {
      return GateTypes.AGE_GATE;
    }

    if (isAuthGated) {
      return GateTypes.AUTH_GATE;
    }
  })();

  useEffect(() => {
    // set initial cookie config client-side
    setCookieConfig({
      [PRIVACY_POLICY_COOKIE_NAME]: getClientCookie(PRIVACY_POLICY_COOKIE_NAME),

      [DOB_COOKIE_NAME]: getClientCookie(DOB_COOKIE_NAME),

      [AUTH_GATE_COOKIE_NAME]: getClientCookie(AUTH_GATE_COOKIE_NAME),
    });
  }, []);

  return {
    type: (() => {
      if (!gateType) return;

      // if gate initially exists, we need to wait until we retrieve user cookies
      // and item.userCanEdit to determine if it passed or do we need to show it at all
      // item.userCanEdit means we still loading the user data
      if (cookieConfig === null || item.userCanEdit === undefined) {
        return GateTypes.LOADING;
      }

      // we do not need to show gate when user can edit
      if (item.userCanEdit) return;

      return gateType;
    })(),

    // used to re-render page when we set the new cookie
    unlock: (name, value, ttl) => {
      setClientCookie(name, value, ttl);

      setCookieConfig((prevConfig) => ({
        ...prevConfig,
        [name]: value,
      }));
    },
  };
};

const getIsAgeGated = ({
  cookie,
  country,
  addon,
}: {
  cookie?: string;
  country: string;
  addon?: NonNullable<AddonsConfig[AddonTypes.AGE_GATE]>;
}) => {
  if (!addon) return { isAgeGated: false, isAgeBlocked: false };

  const ageLimit = addon[country] ?? addon[DEFAULT_TERRITORY_CODE];
  const userAge = resolveAgeFromTimestamp(Number(cookie));

  return {
    isAgeGated: ageLimit && userAge === undefined,
    isAgeBlocked: ageLimit && userAge && userAge < ageLimit,
  };
};

const getIsPrivacyPolicyGated = ({
  cookie,
  country,
}: {
  cookie?: string;
  country: string;
}) => {
  const isPrivacyPolicyGated =
    MUST_ACCEPT_PRIVACY_POLICY_COUNTRIES.includes(country);

  return isPrivacyPolicyGated && cookie !== 'true';
};

const getIsAuthGated = ({
  cookie: cookiePagePath,
  pagePath,
  addon,
}: {
  cookie?: string;
  pagePath: string;
  addon?: NonNullable<AddonsConfig[AddonTypes.AUTH_GATE]>;
}) => Boolean(addon) && pagePath !== cookiePagePath;
